import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import AnalyticsView from "@/components/AnalyticsView";
import EmailTemplatesManager from "@/components/EmailTemplatesManager";
import CustomersManager from "@/components/CustomersManager";
import SubscriptionsManager from "@/components/SubscriptionsManager";
import PlansManager from "@/components/PlansManager";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Building2, Users, Calendar, Euro, Settings, CreditCard, 
  TrendingUp, Package, Edit, Trash2, Plus, Save, ChevronRight,
  Crown, Shield, Store, Cloud, CheckCircle, Eye, EyeOff, Loader2, BarChart3,
  MoreVertical, ExternalLink, Link2, Code, Mail, Send, UserPlus, Bell, FileText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

// Extracted StatCard component to avoid unstable nested component warning
const SuperAdminStatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="hover:shadow-lg transition-shadow">
    <CardContent className="p-4 sm:pt-6">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-slate-500 truncate">{title}</p>
          <p className="text-xl sm:text-3xl font-bold mt-1 truncate" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1 hidden sm:block">{subtitle}</p>}
        </div>
        <div className="p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Read initial tab from URL query param
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "overview");
  
  // Update activeTab when URL changes
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);
  
  // Cloudinary state
  const [cloudinaryConfig, setCloudinaryConfig] = useState({
    cloud_name: "",
    api_key: "",
    api_secret: ""
  });
  const [cloudinaryConfigured, setCloudinaryConfigured] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [savingCloudinary, setSavingCloudinary] = useState(false);

  // Stripe state
  const [stripeConfig, setStripeConfig] = useState({
    secret_key: "",
    publishable_key: "",
    webhook_secret: "",
    connect_client_id: ""
  });
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  const [creatingStripePrice, setCreatingStripePrice] = useState(null);

  // Resend state
  const [resendConfig, setResendConfig] = useState({
    api_key: "",
    sender_email: "onboarding@resend.dev"
  });
  const [resendConfigured, setResendConfigured] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [savingResend, setSavingResend] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");

  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [assignBusinessOpen, setAssignBusinessOpen] = useState(false);
  const [selectedAdminForAssign, setSelectedAdminForAssign] = useState(null);
  const [selectedBusinessesForAssign, setSelectedBusinessesForAssign] = useState([]);
  
  // Firebase Push state
  const [firebaseBusinessId, setFirebaseBusinessId] = useState("");
  const [firebaseUploading, setFirebaseUploading] = useState(false);
  
  // Business settings state (for notification, redirect, recurring)
  const [businessSettings, setBusinessSettings] = useState({
    booking_confirmation_title: "",
    booking_confirmation_body: "",
    reminder_enabled: true,
    reminder_hours: [24],
    reminder_title: "",
    reminder_body: "",
    redirect_url_after_booking: "",
    recurring_appointments_enabled: false
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Load business settings when firebaseBusinessId changes
  useEffect(() => {
    if (firebaseBusinessId) {
      const biz = businesses.find(b => b.business_id === firebaseBusinessId);
      if (biz) {
        const ns = biz.notification_settings || {};
        setBusinessSettings({
          booking_confirmation_title: ns.booking_confirmation_title || "Prenotazione Confermata ✅",
          booking_confirmation_body: ns.booking_confirmation_body || "{service_name} - {booking_date}",
          reminder_enabled: ns.reminder_enabled !== false,
          reminder_hours: ns.reminder_hours || [24],
          reminder_title: ns.reminder_title || "Promemoria Appuntamento 🔔",
          reminder_body: ns.reminder_body || "Domani alle {booking_time}: {service_name}",
          redirect_url_after_booking: biz.redirect_url_after_booking || "",
          recurring_appointments_enabled: biz.recurring_appointments_enabled || false
        });
      }
    }
  }, [firebaseBusinessId, businesses]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, plansRes, bizRes, cloudinaryRes, stripeRes, resendRes, adminsRes] = await Promise.all([
        axios.get(`${API}/superadmin/stats`, { headers }),
        axios.get(`${API}/superadmin/plans`, { headers }),
        axios.get(`${API}/admin/businesses`, { headers }),
        axios.get(`${API}/superadmin/settings/cloudinary`, { headers }).catch(() => ({ data: { configured: false } })),
        axios.get(`${API}/superadmin/settings/stripe`, { headers }).catch(() => ({ data: { configured: false } })),
        axios.get(`${API}/superadmin/settings/resend`, { headers }).catch(() => ({ data: { configured: false } })),
        axios.get(`${API}/superadmin/admins`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setPlans(plansRes.data);
      setBusinesses(bizRes.data);
      setAdmins(adminsRes.data || []);
      
      if (cloudinaryRes.data.configured) {
        setCloudinaryConfigured(true);
        setCloudinaryConfig({
          cloud_name: cloudinaryRes.data.cloud_name || "",
          api_key: cloudinaryRes.data.api_key || "",
          api_secret: ""
        });
      }
      
      if (stripeRes.data.configured) {
        setStripeConfigured(true);
        setStripeConfig({
          secret_key: "",
          publishable_key: stripeRes.data.publishable_key || "",
          webhook_secret: "",
          connect_client_id: stripeRes.data.connect_client_id || ""
        });
      }
      
      if (resendRes.data.configured) {
        setResendConfigured(true);
        setResendConfig({
          api_key: resendRes.data.api_key || "",
          sender_email: resendRes.data.sender_email || "onboarding@resend.dev"
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Accesso non autorizzato");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCloudinary = async () => {
    if (!cloudinaryConfig.cloud_name || !cloudinaryConfig.api_key || !cloudinaryConfig.api_secret) {
      toast.error("Inserisci tutte le credenziali Cloudinary");
      return;
    }
    
    setSavingCloudinary(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/settings/cloudinary`, cloudinaryConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cloudinary configurato con successo!");
      setCloudinaryConfigured(true);
      setCloudinaryConfig({ ...cloudinaryConfig, api_secret: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio delle credenziali");
    } finally {
      setSavingCloudinary(false);
    }
  };

  const handleRemoveCloudinary = async () => {
    if (!confirm("Sei sicuro di voler rimuovere la configurazione Cloudinary?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/superadmin/settings/cloudinary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione Cloudinary rimossa");
      setCloudinaryConfigured(false);
      setCloudinaryConfig({ cloud_name: "", api_key: "", api_secret: "" });
    } catch (error) {
      toast.error("Errore nella rimozione della configurazione");
    }
  };

  const handleSaveStripe = async () => {
    if (!stripeConfig.secret_key || !stripeConfig.publishable_key) {
      toast.error("Inserisci almeno Secret Key e Publishable Key");
      return;
    }
    
    setSavingStripe(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/settings/stripe`, stripeConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stripe configurato con successo!");
      setStripeConfigured(true);
      setStripeConfig({ ...stripeConfig, secret_key: "", webhook_secret: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio delle credenziali Stripe");
    } finally {
      setSavingStripe(false);
    }
  };

  const handleRemoveStripe = async () => {
    if (!confirm("Sei sicuro di voler rimuovere la configurazione Stripe?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/superadmin/settings/stripe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione Stripe rimossa");
      setStripeConfigured(false);
      setStripeConfig({ secret_key: "", publishable_key: "", webhook_secret: "", connect_client_id: "" });
    } catch (error) {
      toast.error("Errore nella rimozione della configurazione");
    }
  };

  const handleCreateStripePrice = async (plan) => {
    if (!stripeConfigured) {
      toast.error("Configura prima Stripe");
      return;
    }
    
    setCreatingStripePrice(plan.plan_id);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/plans/${plan.plan_id}/stripe-price`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Prezzo Stripe creato per ${plan.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione del prezzo Stripe");
    } finally {
      setCreatingStripePrice(null);
    }
  };

  const handleUpdatePlan = async (plan) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/superadmin/plans/${plan.plan_id}`, plan, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Piano aggiornato");
      setEditingPlan(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento del piano");
    }
  };

  // Resend handlers
  const handleSaveResend = async () => {
    if (!resendConfig.api_key || resendConfig.api_key.startsWith("****")) {
      toast.error("Inserisci la API Key Resend");
      return;
    }
    
    setSavingResend(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/settings/resend`, resendConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resend configurato con successo!");
      setResendConfigured(true);
      setResendConfig({ ...resendConfig, api_key: "****" + resendConfig.api_key.slice(-4) });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingResend(false);
    }
  };

  const handleRemoveResend = async () => {
    if (!confirm("Sei sicuro di voler rimuovere la configurazione Resend?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/superadmin/settings/resend`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione Resend rimossa");
      setResendConfigured(false);
      setResendConfig({ api_key: "", sender_email: "onboarding@resend.dev" });
    } catch (error) {
      toast.error("Errore nella rimozione della configurazione");
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Inserisci un'email di test");
      return;
    }
    
    setTestingEmail(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/settings/resend/test`, 
        { email: testEmailAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email di test inviata a ${testEmailAddress}`);
      setTestEmailAddress("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email di test");
    } finally {
      setTestingEmail(false);
    }
  };

  // Admin management handlers
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error("Compila tutti i campi");
      return;
    }
    
    setCreatingAdmin(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/admins`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin creato con successo!");
      setCreateAdminOpen(false);
      setNewAdmin({ name: "", email: "", password: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione dell'admin");
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!confirm("Sei sicuro di voler eliminare questo admin? Verrà rimosso da tutte le aziende assegnate.")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/superadmin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin eliminato");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleToggleAdminActive = async (admin) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/superadmin/admins/${admin.user_id}`, 
        { is_active: !admin.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(admin.is_active ? "Admin disattivato" : "Admin attivato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openAssignBusinessDialog = async (admin) => {
    setSelectedAdminForAssign(admin);
    // Get businesses already assigned to this admin
    const assignedBizIds = businesses
      .filter(b => (b.admin_ids || []).includes(admin.user_id))
      .map(b => b.business_id);
    setSelectedBusinessesForAssign(assignedBizIds);
    setAssignBusinessOpen(true);
  };

  const handleAssignBusinesses = async () => {
    if (!selectedAdminForAssign) return;
    
    try {
      const token = localStorage.getItem("token");
      // For each business, update its admin_ids
      for (const biz of businesses) {
        const isSelected = selectedBusinessesForAssign.includes(biz.business_id);
        const wasAssigned = (biz.admin_ids || []).includes(selectedAdminForAssign.user_id);
        
        if (isSelected !== wasAssigned) {
          const newAdminIds = isSelected
            ? [...(biz.admin_ids || []), selectedAdminForAssign.user_id]
            : (biz.admin_ids || []).filter(id => id !== selectedAdminForAssign.user_id);
          
          await axios.put(`${API}/superadmin/businesses/${biz.business_id}/admins`, 
            { admin_ids: newAdminIds },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      
      toast.success("Aziende assegnate con successo");
      setAssignBusinessOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'assegnazione");
    }
  };

  // Save business settings (notification, redirect, recurring)
  const handleSaveBusinessSettings = async () => {
    if (!firebaseBusinessId) {
      toast.error("Seleziona un'azienda");
      return;
    }
    
    setSavingSettings(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/business/${firebaseBusinessId}/settings`,
        {
          notification_settings: {
            booking_confirmation_title: businessSettings.booking_confirmation_title,
            booking_confirmation_body: businessSettings.booking_confirmation_body,
            reminder_enabled: businessSettings.reminder_enabled,
            reminder_hours: businessSettings.reminder_hours,
            reminder_title: businessSettings.reminder_title,
            reminder_body: businessSettings.reminder_body
          },
          redirect_url_after_booking: businessSettings.redirect_url_after_booking || null,
          recurring_appointments_enabled: businessSettings.recurring_appointments_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Impostazioni salvate con successo!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="superadmin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
              <Crown className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('superadmin.title')}</h1>
              <p className="text-sm text-slate-500">{t('superadmin.subtitle')}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-5xl grid-cols-4 sm:grid-cols-8 h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm px-2 py-2">
              <TrendingUp className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('dashboard.overview')}</span>
            </TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses" className="text-xs sm:text-sm px-2 py-2">
              <Store className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('dashboard.businesses')}</span>
            </TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins" className="text-xs sm:text-sm px-2 py-2">
              <Shield className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Admin</span>
            </TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="text-xs sm:text-sm px-2 py-2">
              <Package className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Piani</span>
            </TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions" className="text-xs sm:text-sm px-2 py-2">
              <CreditCard className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Abbonamenti</span>
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="text-xs sm:text-sm px-2 py-2">
              <Users className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Clienti</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs sm:text-sm px-2 py-2">
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('dashboard.analytics')}</span>
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="text-xs sm:text-sm px-2 py-2">
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('dashboard.settings')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4 sm:mt-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <SuperAdminStatCard 
                title={t('admin.stats.businesses')} 
                value={stats?.total_businesses || 0} 
                icon={Building2} 
                color="#4F46E5"
              />
              <SuperAdminStatCard 
                title={t('admin.stats.users')} 
                value={stats?.total_users || 0} 
                icon={Users} 
                color="#10B981"
              />
              <SuperAdminStatCard 
                title={t('admin.stats.bookings')} 
                value={stats?.total_bookings || 0} 
                icon={Calendar} 
                color="#F59E0B"
              />
              <SuperAdminStatCard 
                title={t('superadmin.stats.mrr')} 
                value={`€${(stats?.monthly_recurring_revenue || 0).toFixed(2)}`} 
                icon={Euro} 
                color="#EC4899"
                subtitle={t('superadmin.stats.mrrSubtitle')}
              />
            </div>

            {/* Revenue from Bookings */}
            <Card className="mb-4 sm:mb-6">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                  {t('superadmin.stats.bookingRevenue')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-4xl font-bold text-green-600">
                  €{(stats?.booking_revenue || 0).toFixed(2)}
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">Totale incassato dalle prenotazioni</p>
              </CardContent>
            </Card>

            {/* Users by Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                    Utenti per Ruolo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.users_by_role && Object.entries(stats.users_by_role).map(([role, count]) => (
                      <div key={role} className="flex items-center justify-between">
                        <span className="capitalize">{role.replace("_", " ")}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Aziende per Piano
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.businesses_by_plan && Object.entries(stats.businesses_by_plan).map(([plan, count]) => (
                      <div key={plan} className="flex items-center justify-between">
                        <span>{plan}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses" className="mt-4 sm:mt-6">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Tutte le Aziende</CardTitle>
                  <CardDescription>{businesses.length} aziende registrate</CardDescription>
                </div>
                <Button onClick={() => navigate("/admin/business/new")} data-testid="create-business-btn" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Azienda
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 sm:space-y-4">
                  {businesses.map((biz) => (
                    <div 
                      key={biz.business_id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors gap-3"
                      onClick={() => navigate(`/admin/business/${biz.business_id}`)}
                      data-testid={`business-row-${biz.business_id}`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0">
                          {biz.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-sm sm:text-base truncate">{biz.name}</h3>
                          <p className="text-xs sm:text-sm text-slate-500 truncate">{biz.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 pl-12 sm:pl-0">
                        <div className="text-left sm:text-right">
                          <p className="text-xs sm:text-sm font-medium">{biz.stats?.total_bookings || 0} prenot.</p>
                          <p className="text-xs text-slate-500">{biz.stats?.total_services || 0} servizi</p>
                        </div>
                        {biz.stats?.total_revenue !== undefined && (
                          <div className="text-left sm:text-right">
                            <p className="text-xs sm:text-sm font-semibold text-green-600">€{biz.stats.total_revenue.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">revenue</p>
                          </div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/business/${biz.business_id}`); }}>
                              <Settings className="h-4 w-4 mr-2" />
                              Gestisci
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(`/book/${biz.slug}`, '_blank'); }}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Apri Pagina Prenotazione
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { 
                              e.stopPropagation(); 
                              navigator.clipboard.writeText(`${window.location.origin}/book/${biz.slug}`);
                              toast.success("Link copiato!");
                            }}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Copia Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { 
                              e.stopPropagation(); 
                              const iframeCode = `<iframe src="${window.location.origin}/book/${biz.slug}" width="100%" height="800" frameborder="0" style="border: none; border-radius: 8px;" title="Prenota un appuntamento"></iframe>`;
                              navigator.clipboard.writeText(iframeCode);
                              toast.success("Codice iframe copiato!");
                            }}>
                              <Code className="h-4 w-4 mr-2" />
                              Copia Codice Iframe
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <ChevronRight className="h-5 w-5 text-slate-400 hidden sm:block" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Management Tab */}
          <TabsContent value="admins" className="mt-4 sm:mt-6">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Gestione Admin
                  </CardTitle>
                  <CardDescription>{admins.length} admin registrati</CardDescription>
                </div>
                <Dialog open={createAdminOpen} onOpenChange={setCreateAdminOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-admin-btn">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Nuovo Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crea Nuovo Admin</DialogTitle>
                      <DialogDescription>
                        Admin potrà gestire le aziende assegnate
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="admin-name">Nome *</Label>
                        <Input
                          id="admin-name"
                          value={newAdmin.name}
                          onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                          placeholder="Mario Rossi"
                          required
                          data-testid="new-admin-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-email">Email *</Label>
                        <Input
                          id="admin-email"
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                          placeholder="admin@esempio.it"
                          required
                          data-testid="new-admin-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-password">Password *</Label>
                        <Input
                          id="admin-password"
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                          placeholder="Almeno 8 caratteri"
                          required
                          data-testid="new-admin-password"
                        />
                      </div>
                      <DialogFooter>
                        <Button type="submit" disabled={creatingAdmin} data-testid="submit-new-admin">
                          {creatingAdmin ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Creazione...
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4 mr-2" />
                              Crea Admin
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {admins.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Nessun admin creato</p>
                    <Button onClick={() => setCreateAdminOpen(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Crea il primo admin
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {admins.map((admin) => (
                      <div 
                        key={admin.user_id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-slate-50 gap-3"
                        data-testid={`admin-row-${admin.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                            {admin.name?.charAt(0).toUpperCase() || "A"}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{admin.name}</h3>
                              <Badge className={admin.is_active !== false ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                                {admin.is_active !== false ? "Attivo" : "Inattivo"}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{admin.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pl-13 sm:pl-0">
                          <Badge variant="outline" className="text-xs">
                            <Building2 className="h-3 w-3 mr-1" />
                            {admin.business_count || 0} aziende
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openAssignBusinessDialog(admin)}
                            data-testid={`assign-biz-${admin.user_id}`}
                          >
                            <Building2 className="h-4 w-4 mr-1" />
                            Assegna
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleToggleAdminActive(admin)}>
                                {admin.is_active !== false ? (
                                  <><EyeOff className="h-4 w-4 mr-2" />Disattiva</>
                                ) : (
                                  <><Eye className="h-4 w-4 mr-2" />Attiva</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteAdmin(admin.user_id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assign Business Dialog */}
            <Dialog open={assignBusinessOpen} onOpenChange={setAssignBusinessOpen}>
              <DialogContent className="max-w-lg max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle>Assegna Aziende a {selectedAdminForAssign?.name}</DialogTitle>
                  <DialogDescription>
                    Seleziona le aziende che questo admin potrà gestire
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {businesses.map((biz) => (
                    <div 
                      key={biz.business_id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedBusinessesForAssign.includes(biz.business_id)
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'hover:border-slate-300'
                      }`}
                      onClick={() => {
                        setSelectedBusinessesForAssign(prev => 
                          prev.includes(biz.business_id)
                            ? prev.filter(id => id !== biz.business_id)
                            : [...prev, biz.business_id]
                        );
                      }}
                      data-testid={`assign-checkbox-${biz.business_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                          selectedBusinessesForAssign.includes(biz.business_id)
                            ? 'bg-primary border-primary text-white'
                            : 'border-slate-300'
                        }`}>
                          {selectedBusinessesForAssign.includes(biz.business_id) && (
                            <CheckCircle className="h-3 w-3" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{biz.name}</p>
                          <p className="text-xs text-slate-500">/book/{biz.slug}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAssignBusinessOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleAssignBusinesses} data-testid="confirm-assign-biz">
                    <Save className="h-4 w-4 mr-2" />
                    Salva Assegnazioni
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans" className="mt-4 sm:mt-6">
            <PlansManager />
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="mt-4 sm:mt-6">
            <SubscriptionsManager plans={plans} />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="mt-4 sm:mt-6">
            <CustomersManager role="superadmin" businesses={businesses} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-4 sm:mt-6">
            <AnalyticsView isAdmin={true} showAllBusinesses={true} />
          </TabsContent>

          {/* Settings Tab - Subscription Plans + Cloudinary */}
          <TabsContent value="settings" className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
            {/* Cloudinary Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Cloud className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Cloudinary - Storage Immagini</CardTitle>
                      <CardDescription>
                        Configura Cloudinary per lo storage persistente delle immagini in produzione
                      </CardDescription>
                    </div>
                  </div>
                  {cloudinaryConfigured && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Configurato
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {cloudinaryConfigured ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Cloudinary è configurato</p>
                        <p className="text-sm text-green-600">
                          Cloud Name: <span className="font-mono">{cloudinaryConfig.cloud_name}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setCloudinaryConfigured(false)}
                        data-testid="edit-cloudinary-btn"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifica
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleRemoveCloudinary}
                        data-testid="remove-cloudinary-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Rimuovi
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                      <p className="text-sm text-amber-800">
                        <strong>Nota:</strong> Senza Cloudinary, le immagini caricate verranno perse ad ogni deploy.
                        Registrati su <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="underline">cloudinary.com</a> per ottenere le credenziali gratuite.
                      </p>
                    </div>
                    
                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="cloud_name">Cloud Name</Label>
                        <Input
                          id="cloud_name"
                          placeholder="Il tuo cloud name"
                          value={cloudinaryConfig.cloud_name}
                          onChange={(e) => setCloudinaryConfig({...cloudinaryConfig, cloud_name: e.target.value})}
                          data-testid="cloudinary-cloud-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="api_key">API Key</Label>
                        <Input
                          id="api_key"
                          placeholder="La tua API Key"
                          value={cloudinaryConfig.api_key}
                          onChange={(e) => setCloudinaryConfig({...cloudinaryConfig, api_key: e.target.value})}
                          data-testid="cloudinary-api-key"
                        />
                      </div>
                      <div>
                        <Label htmlFor="api_secret">API Secret</Label>
                        <div className="relative">
                          <Input
                            id="api_secret"
                            type={showApiSecret ? "text" : "password"}
                            placeholder="Il tuo API Secret"
                            value={cloudinaryConfig.api_secret}
                            onChange={(e) => setCloudinaryConfig({...cloudinaryConfig, api_secret: e.target.value})}
                            data-testid="cloudinary-api-secret"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowApiSecret(!showApiSecret)}
                          >
                            {showApiSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleSaveCloudinary} 
                      disabled={savingCloudinary}
                      className="w-full"
                      data-testid="save-cloudinary-btn"
                    >
                      {savingCloudinary ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Verifica in corso...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salva Configurazione
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stripe Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <CreditCard className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle>Stripe - Pagamenti</CardTitle>
                      <CardDescription>
                        Configura Stripe per gli abbonamenti ricorrenti e i pagamenti
                      </CardDescription>
                    </div>
                  </div>
                  {stripeConfigured && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Configurato
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {stripeConfigured ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Stripe è configurato</p>
                        <p className="text-sm text-green-600">
                          Publishable Key: <span className="font-mono">{stripeConfig.publishable_key?.substring(0, 20)}...</span>
                        </p>
                        {stripeConfig.connect_client_id && (
                          <p className="text-sm text-green-600">
                            Connect Client ID: <span className="font-mono">{stripeConfig.connect_client_id?.substring(0, 15)}...</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setStripeConfigured(false)}
                        data-testid="edit-stripe-btn"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifica
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleRemoveStripe}
                        data-testid="remove-stripe-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Rimuovi
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                      <p className="text-sm text-purple-800">
                        <strong>Ottieni le chiavi da:</strong>{" "}
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">
                          Stripe Dashboard → Developers → API Keys
                        </a>
                      </p>
                      <p className="text-xs text-purple-600 mt-1">
                        Per Stripe Connect: Dashboard → Connect → Settings → Integration
                      </p>
                    </div>
                    
                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="stripe_secret">Secret Key *</Label>
                        <div className="relative">
                          <Input
                            id="stripe_secret"
                            type={showStripeSecret ? "text" : "password"}
                            placeholder="sk_test_... o sk_live_..."
                            value={stripeConfig.secret_key}
                            onChange={(e) => setStripeConfig({...stripeConfig, secret_key: e.target.value})}
                            data-testid="stripe-secret-key"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowStripeSecret(!showStripeSecret)}
                          >
                            {showStripeSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="stripe_publishable">Publishable Key *</Label>
                        <Input
                          id="stripe_publishable"
                          placeholder="pk_test_... o pk_live_..."
                          value={stripeConfig.publishable_key}
                          onChange={(e) => setStripeConfig({...stripeConfig, publishable_key: e.target.value})}
                          data-testid="stripe-publishable-key"
                        />
                      </div>
                      <div>
                        <Label htmlFor="stripe_webhook">Webhook Secret (opzionale)</Label>
                        <Input
                          id="stripe_webhook"
                          type="password"
                          placeholder="whsec_..."
                          value={stripeConfig.webhook_secret}
                          onChange={(e) => setStripeConfig({...stripeConfig, webhook_secret: e.target.value})}
                          data-testid="stripe-webhook-secret"
                        />
                      </div>
                      <div>
                        <Label htmlFor="stripe_connect_id">Connect Client ID (per Stripe Connect)</Label>
                        <Input
                          id="stripe_connect_id"
                          placeholder="ca_..."
                          value={stripeConfig.connect_client_id}
                          onChange={(e) => setStripeConfig({...stripeConfig, connect_client_id: e.target.value})}
                          data-testid="stripe-connect-client-id"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Necessario se vuoi permettere alle aziende di collegare il proprio account Stripe
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleSaveStripe} 
                      disabled={savingStripe}
                      className="w-full"
                      data-testid="save-stripe-btn"
                    >
                      {savingStripe ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Verifica in corso...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salva Configurazione
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resend Email Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Mail className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle>Resend - Invio Email</CardTitle>
                      <CardDescription>
                        Configura Resend per inviare email di conferma prenotazione e promemoria
                      </CardDescription>
                    </div>
                  </div>
                  {resendConfigured && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Configurato
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {resendConfigured ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Resend è configurato</p>
                        <p className="text-sm text-green-600">
                          Sender: <span className="font-mono">{resendConfig.sender_email}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Test Email Section */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <Label className="text-sm font-medium mb-2 block">Invia Email di Test</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="email@test.it"
                          value={testEmailAddress}
                          onChange={(e) => setTestEmailAddress(e.target.value)}
                          className="flex-1"
                          data-testid="test-email-input"
                        />
                        <Button 
                          onClick={handleTestEmail}
                          disabled={testingEmail}
                          data-testid="send-test-email-btn"
                        >
                          {testingEmail ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Send className="h-4 w-4 mr-2" />Invia</>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setResendConfigured(false)}
                        data-testid="edit-resend-btn"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifica
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleRemoveResend}
                        data-testid="remove-resend-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Rimuovi
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mb-4">
                      <p className="text-sm text-emerald-800">
                        <strong>Ottieni la API Key da:</strong>{" "}
                        <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">
                          Resend Dashboard → API Keys
                        </a>
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">
                        Registrati gratuitamente su resend.com. La chiave inizia con re_...
                      </p>
                    </div>
                    
                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="resend_api_key">API Key *</Label>
                        <div className="relative">
                          <Input
                            id="resend_api_key"
                            type={showResendKey ? "text" : "password"}
                            placeholder="re_..."
                            value={resendConfig.api_key}
                            onChange={(e) => setResendConfig({...resendConfig, api_key: e.target.value})}
                            data-testid="resend-api-key"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowResendKey(!showResendKey)}
                          >
                            {showResendKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="sender_email">Email Mittente</Label>
                        <Input
                          id="sender_email"
                          type="email"
                          placeholder="noreply@tuodominio.it"
                          value={resendConfig.sender_email}
                          onChange={(e) => setResendConfig({...resendConfig, sender_email: e.target.value})}
                          data-testid="resend-sender-email"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          In modalità test usa onboarding@resend.dev
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleSaveResend} 
                      disabled={savingResend}
                      className="w-full"
                      data-testid="save-resend-btn"
                    >
                      {savingResend ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Verifica in corso...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salva Configurazione
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Firebase Push Notifications & Business Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Bell className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Impostazioni Azienda</CardTitle>
                    <CardDescription>
                      Configura Firebase, notifiche, redirect e appuntamenti ricorrenti per ogni azienda
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Seleziona Azienda</Label>
                  <Select value={firebaseBusinessId} onValueChange={setFirebaseBusinessId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona un'azienda..." />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map(b => (
                        <SelectItem key={b.business_id} value={b.business_id}>
                          {b.name} {b.firebase_project_id && <span className="text-green-600 ml-2">✓ Firebase</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {firebaseBusinessId && (
                  <div className="space-y-6">
                    {/* Firebase Service Account */}
                    <div className="border rounded-lg p-4">
                      <Label className="font-semibold">Firebase Service Account (JSON)</Label>
                      <p className="text-sm text-slate-500 mb-2">
                        Firebase Console → Impostazioni → Account di servizio → Genera nuova chiave privata
                      </p>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-primary transition-colors">
                        <input
                          type="file"
                          id="firebase-json-upload-admin"
                          accept=".json"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !firebaseBusinessId) return;
                            
                            const reader = new FileReader();
                            reader.onload = async (event) => {
                              try {
                                const jsonContent = event.target?.result;
                                const parsed = JSON.parse(jsonContent);
                                if (!parsed.project_id || !parsed.private_key) {
                                  toast.error("File JSON non valido");
                                  return;
                                }
                                
                                setFirebaseUploading(true);
                                try {
                                  const token = localStorage.getItem("token");
                                  const res = await axios.put(
                                    `${API}/admin/business/${firebaseBusinessId}/firebase-config`,
                                    { service_account_json: jsonContent },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`Firebase configurato! Progetto: ${res.data.project_id}`);
                                  fetchData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Errore nel salvataggio");
                                } finally {
                                  setFirebaseUploading(false);
                                }
                              } catch (err) {
                                toast.error("File JSON non valido");
                              }
                            };
                            reader.readAsText(file);
                          }}
                        />
                        <label htmlFor="firebase-json-upload-admin" className="cursor-pointer">
                          <div className="flex flex-col items-center gap-2">
                            {firebaseUploading ? (
                              <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            ) : (
                              <FileText className="h-8 w-8 text-slate-400" />
                            )}
                            <span className="text-sm text-slate-600">
                              Clicca per caricare <strong>serviceAccountKey.json</strong>
                            </span>
                          </div>
                        </label>
                      </div>
                      
                      {businesses.find(b => b.business_id === firebaseBusinessId)?.firebase_project_id && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mt-3">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm text-green-800">
                            Progetto: {businesses.find(b => b.business_id === firebaseBusinessId)?.firebase_project_id}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Notification Text Settings */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <Label className="font-semibold">Testo Notifiche</Label>
                      
                      <div>
                        <Label className="text-sm">Titolo conferma prenotazione</Label>
                        <Input
                          placeholder="Prenotazione Confermata ✅"
                          value={businessSettings.booking_confirmation_title}
                          onChange={(e) => setBusinessSettings({...businessSettings, booking_confirmation_title: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Testo conferma (usa {"{service_name}"}, {"{booking_date}"})</Label>
                        <Input
                          placeholder="{service_name} - {booking_date}"
                          value={businessSettings.booking_confirmation_body}
                          onChange={(e) => setBusinessSettings({...businessSettings, booking_confirmation_body: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* Reminder Settings */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <Label className="font-semibold">Promemoria</Label>
                      
                      <div className="flex items-center gap-3">
                        <Switch 
                          id="reminder-enabled-superadmin" 
                          checked={businessSettings.reminder_enabled}
                          onCheckedChange={(checked) => setBusinessSettings({...businessSettings, reminder_enabled: checked})}
                        />
                        <label htmlFor="reminder-enabled-superadmin" className="text-sm">Abilita promemoria</label>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Invia promemoria (ore prima)</Label>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {[24, 12, 6, 2, 1].map(h => (
                            <label key={h} className="flex items-center gap-1 text-sm">
                              <input 
                                type="checkbox" 
                                checked={businessSettings.reminder_hours.includes(h)}
                                onChange={(e) => {
                                  const hours = e.target.checked 
                                    ? [...businessSettings.reminder_hours, h]
                                    : businessSettings.reminder_hours.filter(x => x !== h);
                                  setBusinessSettings({...businessSettings, reminder_hours: hours});
                                }}
                                className="rounded" 
                              />
                              {h}h
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Titolo promemoria</Label>
                        <Input
                          placeholder="Promemoria Appuntamento 🔔"
                          value={businessSettings.reminder_title}
                          onChange={(e) => setBusinessSettings({...businessSettings, reminder_title: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Testo promemoria</Label>
                        <Input
                          placeholder="Domani alle {booking_time}: {service_name}"
                          value={businessSettings.reminder_body}
                          onChange={(e) => setBusinessSettings({...businessSettings, reminder_body: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* URL Redirect */}
                    <div className="border rounded-lg p-4">
                      <Label className="font-semibold">URL Redirect dopo prenotazione</Label>
                      <p className="text-xs text-slate-500 mb-2">Il cliente verrà reindirizzato a questo URL dopo aver completato la prenotazione</p>
                      <Input
                        placeholder="https://tuosito.com/grazie"
                        value={businessSettings.redirect_url_after_booking}
                        onChange={(e) => setBusinessSettings({...businessSettings, redirect_url_after_booking: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Recurring Appointments */}
                    <div className="border rounded-lg p-4">
                      <Label className="font-semibold">Appuntamenti Ricorrenti</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Switch 
                          id="recurring-enabled-superadmin" 
                          checked={businessSettings.recurring_appointments_enabled}
                          onCheckedChange={(checked) => setBusinessSettings({...businessSettings, recurring_appointments_enabled: checked})}
                        />
                        <label htmlFor="recurring-enabled-superadmin" className="text-sm">
                          Permetti ai clienti di prenotare appuntamenti ricorrenti
                        </label>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full" 
                      onClick={handleSaveBusinessSettings}
                      disabled={savingSettings}
                    >
                      {savingSettings ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Salva Impostazioni Azienda
                    </Button>
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Importante per iOS</h4>
                  <p className="text-sm text-blue-800">
                    Carica la <strong>APNs Auth Key</strong> in Firebase Console → Cloud Messaging → Configurazione app Apple.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Email Templates Manager */}
            <EmailTemplatesManager role="superadmin" />

            {/* Plans Manager (Gestione Piani Completa) */}
            <PlansManager />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
